import { ChangeEvent, useEffect, useState } from 'react';
import Dropdown from '../../components/Dropdown/Dropdown';
import dayjs, { Dayjs } from 'dayjs';
import {
  SideContainers,
  DashboardMainPanel,
  DropdownWrapperMedium,
  H1,
  HeaderWrapper,
  DashboardBody,
  MoaDashboardHeader,
  DashboardRow,
} from './MoaDashboard.styles';
import { DAY_FILTER_TYPE, DayFilterTypeKeys } from '../../constants/ApiEnums';
import {
  useDashboard,
  useExtractionsAndActivationsDetails,
  useNullSpecsDetails,
  useMenuNegativePricingDetails,
} from '../../services/dashboard/dashboard.service';
import TranslationsContainer from './TranslationsContainer/TranslationsContainer';
import ExtractionsContainer from './ExtractionsContainer/ExtractionsContainer';
import ActivationsContainer from './ActivationsContainer/ActivationsContainer';
import PublishedContainer from './PublishedContainer/PublishedContainer';
import NullSpecs from './NullSpecsContainer/NullSpecsContainer';
import NegativePriceMetrics from './NegativePriceMetricsContainer/NegativePriceMetricsContainer';
import PricingMetrics from './PricingMetricsContainer/PricingMetrics';
import SideDrawer from '../../components/Drawer/Drawer';
import { DashboardFilter } from "../../services/dashboard/dashboard.types";

const MoaDashboard = () => {

  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState('');
  const [drawerColumnDefs, setDrawerColumnDefs] = useState();
  const [fetchNullSpecs, setFetchNullSpecs] = useState(false);
  const [fetchNegativePrices, setFetchNegativePrices] = useState(false);
  const [fetchExtractionsFailure, setFetchExtractionsFailure] = useState<boolean>(false);
  const [extractionAndActivationsJobId, setExtractionAndActivationsJobId] = useState<string>('');
  const [requestUserName, setRequestUserName] = useState<string>('');
  const [rangeFilter, setRangeFilter] = useState<string>('TODAY');
  const [from, setFrom] = useState<Dayjs | undefined>(dayjs().startOf('day'));
  const [until, setUntil] = useState<Dayjs | undefined>(dayjs().endOf('day').set('minutes', 55));
  const filter: DashboardFilter = {
    from: from,
    to: until?.get('minutes') === 55 ? until?.set('minutes', 59) : until
  }

  const { data: dashboard, isLoading } = useDashboard({
    dayFilter: rangeFilter,
    from: filter.from?.toISOString(),
    to: filter.to?.toISOString()
  });

  const { data: nullSpecMetricsData } = useNullSpecsDetails(
    {
      from: filter.from?.toISOString(),
      to: filter.to?.toISOString()
    },
    fetchNullSpecs
  );

  const { data: extractionsFailures } = useExtractionsAndActivationsDetails(
    extractionAndActivationsJobId,
    drawerType,
    fetchExtractionsFailure
  );

  const { data: negativePriceMetricsData } = useMenuNegativePricingDetails(
    {
      from: filter.from?.toISOString(),
      to: filter.to?.toISOString()
    },
    fetchNegativePrices
  );

  const [drawerData, setDrawerData] = useState<any[]>([]);

  useEffect(() => {
    if (nullSpecMetricsData) {
      setDrawerData(nullSpecMetricsData.data);
      setFetchNullSpecs(false);
    }
    if (extractionsFailures) {
      setDrawerData(extractionsFailures.data);
      setFetchExtractionsFailure(false);
    }
    if (negativePriceMetricsData) {
      setDrawerData(negativePriceMetricsData.data);
      setFetchNegativePrices(false);
    }
  }, [nullSpecMetricsData, extractionsFailures, negativePriceMetricsData]);

  const handleRangeFilter = (event: ChangeEvent<HTMLSelectElement>) => {
    setRangeFilter(event.target.value);

    let numberOfDays = getAmountOfDays(event.target.value);
    setFrom(dayjs().startOf('day').subtract(numberOfDays, 'days'));
    setUntil(dayjs().endOf('day').subtract(numberOfDays, 'days').set('minutes', 55));
  };

  const buildOptions = () => {
    let options = [
      { value: 'TODAY', label: 'Today' },
      { value: 'YESTERDAY', label: 'Yesterday' },
      { value: 'TWO_DAYS', label: 'Two Days Ago' },
      { value: 'THREE_DAYS', label: 'Three Days Ago' },
      { value: 'FOUR_DAYS', label: 'Four Days Ago' },
      { value: 'FIVE_DAYS', label: 'Five Days Ago' },
      { value: 'SIX_DAYS', label: 'Six Days Ago' },
      { value: 'SEVEN_DAYS', label: 'Seven Days Ago' },
    ];

    return options.map((value) => {
      let today = dayjs().startOf('day');
      let amountToSubtract = getAmountOfDays(value.value);
      let result = today.subtract(amountToSubtract, 'days');
      let label = `${value.label} (${result.format('MM/DD')})`;

      return { value: value.value, label: label };
    });
  };

  const getAmountOfDays = (filter: string) => {
    return DAY_FILTER_TYPE[filter as DayFilterTypeKeys];
  };

  const toggleDrawer = (newOpen: boolean) => {
    setOpenDrawer(newOpen);
  };

  const drawerTitles: any = {
    nullSpecs: 'Missing Specs',
    extract: 'Extractions Failures',
    activate: 'Activations Failures',
    couponsExtractions: 'Coupons Extractions Failures',
    negativePrices: 'Negative Price Items',
  };

  return (
    <>
      <MoaDashboardHeader>
        <HeaderWrapper data-testid='DashboardTitle'>
          <H1>Dashboard</H1>
        </HeaderWrapper>
        <HeaderWrapper>
          <DropdownWrapperMedium>
            <Dropdown
              hidePlaceholder={true}
              label={''}
              id='daysFilter'
              dataTestId='dateRangeDropDown'
              options={buildOptions()}
              value={rangeFilter}
              onChange={handleRangeFilter}
            />
          </DropdownWrapperMedium>
        </HeaderWrapper>
      </MoaDashboardHeader>
      <DashboardBody>
        <DashboardMainPanel>
          <DashboardRow>
            <PublishedContainer
                filter={filter}
                setFrom={setFrom}
                setUntil={setUntil}
                events={dashboard?.data.published.events ?? 0}
                isLoading={isLoading}
            />
            <TranslationsContainer
                menuTranslation={dashboard?.data.translations.menus ?? { total: 0, processed: 0, failed: 0 }}
                promotionTranslation={dashboard?.data.translations.promotions ?? { total: 0, processed: 0, failed: 0 }}
                eventTranslation={dashboard?.data.translations.events ?? { total: 0, processed: 0, failed: 0 }}
                filter={filter}
                isLoading={isLoading}
            />
          </DashboardRow>
          <DashboardRow>
            <ExtractionsContainer
              inProgress={dashboard?.data.extractions.inProgress ?? []}
              upNext={dashboard?.data.extractions.upNext ?? []}
              done={dashboard?.data.extractions.done ?? []}
              setOpenDrawer={setOpenDrawer}
              setDrawerType={setDrawerType}
              setDrawerColumnDefs={setDrawerColumnDefs}
              setFetchExtractionsFailure={setFetchExtractionsFailure}
              setExtractionAndActivationsJobId={setExtractionAndActivationsJobId}
              setRequestUserName={setRequestUserName}
            />
            <ActivationsContainer
              inProgress={dashboard?.data.activations.inProgress ?? []}
              upNext={dashboard?.data.activations.upNext ?? []}
              done={dashboard?.data.activations.done ?? []}
              setOpenDrawer={setOpenDrawer}
              setDrawerType={setDrawerType}
              setDrawerColumnDefs={setDrawerColumnDefs}
              setFetchExtractionsFailure={setFetchExtractionsFailure}
              setExtractionAndActivationsJobId={setExtractionAndActivationsJobId}
              setRequestUserName={setRequestUserName}
            />
          </DashboardRow>
        </DashboardMainPanel>
        <SideContainers>
          <NullSpecs
            nullSpecMetrics={dashboard?.data.nullSpecMetrics}
            setOpenDrawer={setOpenDrawer}
            setDrawerType={setDrawerType}
            isLoading={isLoading}
            setFetchNullSpecs={setFetchNullSpecs}
            setDrawerColumnDefs={setDrawerColumnDefs}
          />
          <PricingMetrics
            pricingMetrics={dashboard?.data.pricingMetrics}
            bundlePricingMetrics={dashboard?.data.bundlePricingMetrics}
            from={from}
            to={until}
            isDashboardLoading={isLoading}
          />
          <NegativePriceMetrics
            negativePriceMetrics={dashboard?.data.negativePriceMetrics}
            setOpenDrawer={setOpenDrawer}
            setDrawerType={setDrawerType}
            isLoading={isLoading}
            setFetchNegativePrices={setFetchNegativePrices}
            setDrawerColumnDefs={setDrawerColumnDefs}
          />
        </SideContainers>
        <SideDrawer
          open={openDrawer}
          toggleDrawer={toggleDrawer}
          title={drawerTitles[drawerType] || ''}
          columnDefs={drawerColumnDefs}
          rowData={drawerData}
          drawerType={drawerType}
          from={from}
          requestUserName={requestUserName}
        />
      </DashboardBody>
    </>
  );
};

export default MoaDashboard;